import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { VirtualItem } from '@tanstack/react-virtual';
import React, { forwardRef, ReactNode, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { Dropdown } from '@/components/Dropdown';
import { Loader } from '@/components/Loader';
import { CELL_WIDTHS, ROW_HEIGHT } from '@/components/table/constants';
import { TableCellContent } from '@/components/table/TableCellContent';
import {
  CellAlignment,
  Column,
  Columns,
  InternalTData,
  TableBodyHoverProps,
  TableProps,
} from '@/components/table/types';
import { applyColumnWidth } from '@/components/table/utils/columnWidth';
import { cn } from '@/utils/classname';

interface TableBodyProps<TData> extends TableBodyHoverProps {
  selectedRows: string[];
  data: InternalTData<TData>[];
  rowActions?: TableProps<TData>['rowActions'];
  rowData: InternalTData<TData>;
  rowIndex: number;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-error
  virtualRow: VirtualItem<Element>;
  isChecked: boolean;
  handleRowClick: (
    idx: number,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void;
  enableCheckbox: boolean;
  isLoading: boolean;
  columns: Columns<TData>;
  isClickable: boolean;
  expandedGroups: string[];
  toggleExpandedGroup: (group?: string) => void;
  groupBy?: TableProps<TData>['groupBy'];
  rowClassName?: (rowData: TData) => string;
  /** Return extra HTML attributes for the <tr> (e.g. aria-busy for pending rows). */
  getRowProps?: (rowData: TData) => Record<string, unknown>;
  sort?: TableProps<TData>['sort'];
  inspecting: number | null;
  rowHeight?: number;
  tabIndex: number;
  isLastRow?: boolean;
  hasSortableColumn?: boolean;
}

/**
 * CheckboxCell component with indeterminate state support
 * Renders a custom checkbox with three states: unchecked, indeterminate, checked
 */
function CheckboxCell({
  checked,
  indeterminate,
}: {
  checked: boolean;
  indeterminate: boolean;
}) {
  const ariaLabel = indeterminate
    ? 'Partially selected'
    : checked
      ? 'Selected'
      : 'Not selected';

  return (
    <label
      className="cursor-pointer"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={e => {
        // Prevent default label behavior and let parent Td handle it
        e.preventDefault();
        // Don't stopPropagation - let it bubble to Td's onClick
      }}
      onKeyDown={e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
    >
      <div
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        className="mr-0 flex size-6 items-center p-1"
      >
        {checked || indeterminate ? (
          <div className="border-chariot-brand-primary bg-chariot-brand-primary flex size-4 items-center justify-center rounded-[2px] border-2">
            {indeterminate ? (
              <div className="h-0.5 w-2 bg-white" />
            ) : (
              <svg
                className="size-3 stroke-[3px] text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        ) : (
          <div className="size-4 rounded-[2px] border-2 border-gray-500" />
        )}
      </div>
    </label>
  );
}

export const TableBody = forwardRef(function TableBody<TData>(
  props: TableBodyProps<TData>,
  ref: React.ForwardedRef<HTMLTableRowElement>
) {
  const {
    selectedRows,
    data,
    rowActions,
    rowData,
    rowIndex,
    virtualRow,
    isChecked,
    handleRowClick,
    enableCheckbox,
    isLoading,
    columns,
    isClickable,
    expandedGroups,
    toggleExpandedGroup,
    rowClassName,
    sort,
    inspecting,
    rowHeight,
    tabIndex,
    isLastRow,
    hoveredRowIndex,
    setHoveredRowIndex,
    hasSortableColumn,
    getRowProps,
  } = props;

  // CRITICAL: All hooks MUST be called before any early returns
  // Hover state is now controlled by parent Table component
  const handleMouseEnter = useCallback(() => {
    if (!isClickable) return;
    setHoveredRowIndex(rowIndex);
  }, [isClickable, rowIndex, setHoveredRowIndex]);

  const handleMouseLeave = useCallback(() => {
    if (!isClickable) return;
    setHoveredRowIndex(null);
  }, [isClickable, setHoveredRowIndex]);

  // Null safety guard - early return AFTER hooks
  if (!rowData) {
    console.warn('TableBody: Received undefined rowData, skipping render');
    return null;
  }

  const isFirstRow = rowIndex === 0;

  // Check if this is a group header row from vulnerability grouping
  const groupableRow = rowData as Record<string, unknown>;
  const isGroupHeader = !!groupableRow._isGroupHeader;
  const isChild = !!groupableRow._isChild;
  const isExpanded = !!groupableRow._isExpanded;

  // T012: Compute checkbox state for group headers
  let checkboxState: { checked: boolean; indeterminate: boolean } = {
    checked: isChecked,
    indeterminate: false,
  };

  if (isGroupHeader) {
    const groupKey = groupableRow._groupKey as string;
    // Find all children of this group in the flat data
    const childrenKeys: string[] = [];
    for (let i = rowIndex + 1; i < data.length; i++) {
      const nextRow = data[i] as Record<string, unknown>;
      if (nextRow._isGroupHeader) break;
      if (nextRow._isChild && nextRow._groupKey === groupKey) {
        childrenKeys.push(nextRow._key as string);
      }
    }

    if (childrenKeys.length > 0) {
      const selectedChildCount = childrenKeys.filter(key =>
        selectedRows.includes(key)
      ).length;

      if (selectedChildCount === 0) {
        checkboxState = { checked: false, indeterminate: false };
      } else if (selectedChildCount === childrenKeys.length) {
        checkboxState = { checked: true, indeterminate: false };
      } else {
        checkboxState = { checked: false, indeterminate: true };
      }
    }
  }

  if (rowData?._type === 'colgroup') {
    const GroupName = rowData?._label || 'Group';

    const isExpanded = expandedGroups.includes(GroupName);
    return (
      <tr
        ref={ref}
        tabIndex={tabIndex}
        className="border-default bg-layer2 focus-visible:bg-chariot-background-secondary cursor-pointer border-t text-sm font-semibold"
        key={`row-${rowIndex}`}
        style={{
          height: `${virtualRow.size}px`,
        }}
        onClick={() => toggleExpandedGroup(GroupName)}
      >
        <th className="">
          <ChevronDownIcon
            className={cn('m-auto size-4', !isExpanded && 'rotate-180')}
          />
        </th>
        <th
          className="text-left"
          colSpan={
            columns.length + (enableCheckbox ? 1 : 0) + (rowActions ? 1 : 0)
          }
          scope="colgroup"
        >
          <Loader isLoading={isLoading}>
            <div className="flex flex-row items-center space-x-2">
              {rowData?._icon}
              <p>{GroupName}</p>
            </div>
          </Loader>
        </th>
      </tr>
    );
  }

  return (
    <tr
      ref={ref}
      tabIndex={tabIndex}
      key={`row-${rowIndex}`}
      data-testid={`row-${rowData?._key}`}
      data-row-index={rowIndex}
      data-index={virtualRow.index}
      {...(getRowProps && rowData ? getRowProps(rowData) : {})}
      style={{
        height: `${virtualRow.size}px`,
      }}
      className={twMerge(
        'transition-colors duration-150 focus-visible:outline-hidden',
        // Group header styling: bold and subtle background
        isGroupHeader && 'bg-default/5 font-semibold',
        // Child row styling: subtle indent visual
        isChild &&
          'bg-chariot-background-secondary/30 border-l-brand/30 border-l-2',
        // Regular row hover (not group headers)
        !isGroupHeader &&
          hoveredRowIndex === rowIndex &&
          'bg-chariot-background-secondary',
        inspecting === Number(rowData?._idx) &&
          'is-selected group bg-chariot-stroke text-chariot-text-primary',
        isChecked && 'bg-chariot-background-secondary',
        isChecked && inspecting && 'bg-chariot-background-secondary',
        isClickable && 'cursor-pointer',
        isLastRow && 'border-chariot-stroke border-b',
        rowClassName && rowClassName(rowData)
      )}
      onClick={event => {
        if (!rowData) return;
        const groupableClickRow = rowData as Record<string, unknown>;
        const isGroupHeaderRow = !!groupableClickRow._isGroupHeader;
        if (!rowData._idx && !isGroupHeaderRow) {
          return;
        }

        event.preventDefault();
        const clickIdx = isGroupHeaderRow
          ? virtualRow.index
          : Number(rowData._idx);
        if (isClickable) {
          handleRowClick(clickIdx, event);
        } else if (enableCheckbox) {
          // Handle checkbox clicks even when row is not clickable
          const target = event.target as HTMLElement;
          const clickedCheckbox = target?.closest?.('[data-checkbox]');
          if (clickedCheckbox) {
            handleRowClick(clickIdx, event);
          }
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {enableCheckbox && (
        <Td
          data-checkbox
          data-testid="checkbox-cell"
          align="center"
          isFirstRow={isFirstRow}
          isLoading={isLoading}
          rowHeight={rowHeight}
          fixedWidth={CELL_WIDTHS.checkbox.fixedWidth}
          minWidth={CELL_WIDTHS.checkbox.minWidth}
          maxWidth={CELL_WIDTHS.checkbox.maxWidth}
          onClick={e => {
            if (!rowData) {
              e.stopPropagation();
              return;
            }
            const checkboxGroupableRow = rowData as Record<string, unknown>;
            const isGroupHeaderRow = !!checkboxGroupableRow._isGroupHeader;
            if (!rowData._idx && !isGroupHeaderRow) {
              e.stopPropagation(); // Still prevent drawer
              return;
            }

            // Always prevent drawer from opening
            e.stopPropagation();

            const clickIdx = isGroupHeaderRow
              ? virtualRow.index
              : Number(rowData._idx);
            handleRowClick(
              clickIdx,
              e as unknown as React.MouseEvent<HTMLTableRowElement, MouseEvent>
            );
          }}
          className="hover:bg-chariot-background-secondary/50 cursor-pointer transition-colors duration-150"
        >
          <Loader isLoading={isLoading}>
            <CheckboxCell
              checked={checkboxState.checked}
              indeterminate={checkboxState.indeterminate}
            />
          </Loader>
        </Td>
      )}
      {columns.map((col, colIndex) => {
        const isFirstColumn = colIndex === 0;
        const stickyCellClass = col.sticky
          ? twMerge(
              // Base positioning + stacking
              col.sticky === 'right' &&
                'sticky right-0 z-10 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]',
              col.sticky === 'left' &&
                'sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
              // Opaque bg matching current row state (mirror the tr's bg classes)
              'bg-chariot-background-primary',
              isGroupHeader && 'bg-default/5',
              isChild && 'bg-chariot-background-secondary/30',
              !isGroupHeader &&
                hoveredRowIndex === rowIndex &&
                'bg-chariot-background-secondary',
              inspecting === Number(rowData?._idx) &&
                'bg-chariot-stroke text-chariot-text-primary',
              isChecked && 'bg-chariot-background-secondary'
            )
          : '';
        return (
          <Td
            key={`row-${rowIndex}-${colIndex}${String(col.id)}`}
            column={col}
            className={twMerge(
              col.className,
              // Add left padding for child rows in first column
              isFirstColumn && isChild && 'pl-8',
              stickyCellClass
            )}
            isFirstRow={isFirstRow}
            isLoading={isLoading}
            align={col.align}
            sort={sort}
            id={col.id as keyof TData}
            rowHeight={rowHeight}
          >
            <Loader isLoading={isLoading}>
              {isFirstColumn && isGroupHeader ? (
                // Group header: render chevron icon before content
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDownIcon className="size-4 shrink-0" />
                  ) : (
                    <ChevronRightIcon className="size-4 shrink-0" />
                  )}
                  <TableCellContent
                    inspecting={inspecting === Number(rowData?._idx)}
                    col={col}
                    item={rowData}
                    selectedRowsData={selectedRows
                      .map(key => data.find(item => item._key === key))
                      .filter(
                        (item): item is InternalTData<TData> =>
                          item !== undefined
                      )}
                    isLoading={col.isLoading ? col.isLoading(rowData) : false}
                    onClick={
                      col.onClick ? () => col?.onClick?.(rowData) : undefined
                    }
                  />
                </div>
              ) : (
                // Regular cell content
                <TableCellContent
                  inspecting={inspecting === Number(rowData?._idx)}
                  col={col}
                  item={rowData}
                  selectedRowsData={selectedRows
                    .map(key => data.find(item => item._key === key))
                    .filter(
                      (item): item is InternalTData<TData> => item !== undefined
                    )}
                  isLoading={col.isLoading ? col.isLoading(rowData) : false}
                  onClick={
                    col.onClick ? () => col?.onClick?.(rowData) : undefined
                  }
                />
              )}
            </Loader>
          </Td>
        );
      })}
      {rowActions &&
        (() => {
          const actions = rowActions(rowData);
          return (
            <Td
              align="center"
              isFirstRow={isFirstRow}
              isLoading={isLoading}
              rowHeight={rowHeight}
              fixedWidth={CELL_WIDTHS.actions.fixedWidth}
              minWidth={CELL_WIDTHS.actions.minWidth}
              maxWidth={CELL_WIDTHS.actions.maxWidth}
            >
              {actions.menu?.items && actions.menu.items.length > 0 && (
                <Loader isLoading={isLoading}>
                  <Dropdown
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    startIcon={
                      <EllipsisVerticalIcon className="m-auto size-6" />
                    }
                    styleType="none"
                    {...actions}
                  />
                </Loader>
              )}
            </Td>
          );
        })()}
      {!rowActions && hasSortableColumn && (
        <Td
          align="center"
          isFirstRow={isFirstRow}
          isLoading={isLoading}
          rowHeight={rowHeight}
          fixedWidth={CELL_WIDTHS.actions.fixedWidth}
          minWidth={CELL_WIDTHS.actions.minWidth}
          maxWidth={CELL_WIDTHS.actions.maxWidth}
        >
          <></>
        </Td>
      )}
    </tr>
  );
}) as <TData>(
  props: TableBodyProps<TData> & {
    ref?: React.ForwardedRef<HTMLTableRowElement>;
  }
) => React.ReactElement;

function Td<TData>(props: {
  column?: Column<TData>; // NEW: Pass column config for proper width application
  id?: keyof TData;
  className?: string;
  align?: CellAlignment;
  sort?: TableProps<TData>['sort'];
  colSpan?: number;
  children: ReactNode;
  isFirstRow: boolean;
  isLoading: boolean;
  rowHeight?: number;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  'data-testid'?: string;
  'data-checkbox'?: boolean;
  // Deprecated props (maintain backward compatibility)
  fixedWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}) {
  // Use centralized width application if column provided
  const widthStyle = props.column
    ? applyColumnWidth(props.column, {})
    : {
        ...(props.fixedWidth && {
          width: props.fixedWidth,
          minWidth: props.fixedWidth,
          maxWidth: props.fixedWidth,
        }),
        ...(!props.fixedWidth &&
          props.minWidth && { minWidth: props.minWidth }),
        ...(!props.fixedWidth &&
          props.maxWidth && { maxWidth: props.maxWidth }),
      };

  // Determine if we should truncate or allow wrapping
  const shouldTruncate = !props.column?.wrapLines;

  return (
    <td
      colSpan={props.colSpan}
      className={cn(
        'relative max-w-full pr-1 pl-2 text-sm',
        shouldTruncate && 'truncate',
        props.align === 'center' && 'pr-3 text-center *:m-auto',
        props.align === 'right' && 'text-right',
        props.className
      )}
      style={{
        height: props.rowHeight ?? ROW_HEIGHT,
        ...widthStyle,
      }}
      onClick={props.onClick}
      data-testid={props['data-testid']}
      data-checkbox={props['data-checkbox'] || undefined}
    >
      <div
        className={cn([
          !props.isFirstRow &&
            'border-chariot-stroke absolute top-0 left-0 w-full border-t',
          'flex items-center',
        ])}
      />
      {props.children}
    </td>
  );
}
